import { useCallback, useRef } from 'react'

import type { Envelope, HelmetCommandIntent } from '@libs/realtime'

import { useLiveChannel } from '../../../../hooks/useLiveChannel'
import { getLiveChannelUrl } from '../../../../services/auth/config'

/**
 * Operator → helmet commands.
 *
 * Deliberately separate from `useRobotCommandSender` rather than an extra branch inside it.
 *
 * Both device kinds publish to the same `robot:{id}:command` topic, but the payloads are different
 * domains, not different spellings: a robot takes `{action}` — drive, e-stop, patrol, posture — and
 * a helmet takes `{kind}` — torch, photo, record, stream, call. There is no sensible mapping
 * between them, and a helmet cannot honour a drive command at all.
 *
 * Until this existed, the console published a robot `{action}` at a helmet, `helmetDownWire`
 * returned null, and the frame was dropped **after** the broker had already accepted and routed it
 * — so the operator saw no error and no acknowledgement while nothing reached the device.
 */
/** What the broker's synthesised call acks carry. `reason` is present only on a bad outcome. */
export interface HelmetAckPayload {
  kind?: string
  state?: string
  reason?: string
  roomId?: string
  viewerId?: string
  /** On a `stream_limit` refusal: the cap that was hit. */
  limit?: number
}

export interface UseHelmetCommandSenderOptions {
  robotId: string | null
  enabled?: boolean
  /** Surfaces `robot_offline` so the UI can say the helmet is not connected. */
  onOffline?: () => void
  /**
   * EVERY other rejection the broker sends back, by code.
   *
   * Without this, only `robot_offline` was ever surfaced and every other refusal was swallowed. A
   * viewer pressing Accept gets `forbidden_role` — the broker refuses a `command` from a read-only
   * role — and the call panel would sit on "Getting your microphone ready…" for ever, because
   * nothing else in the handshake ever answers.
   */
  onRejected?: (code: string) => void
  /**
   * Acknowledgements for commands sent on this channel.
   *
   * The SOS call needs these and nothing else does yet. Its whole handshake is broker-side —
   * prepared, joined, unanswered, ended — and an ack is the only way any of it reaches the operator
   * who placed the call. Without it a call that the firmware never answered looks identical to one
   * still ringing, for ever.
   */
  onAck?: (payload: HelmetAckPayload, frame: Envelope) => void
}

export interface UseHelmetCommandSender {
  connected: boolean
  send: (intent: HelmetCommandIntent) => void
}

export function useHelmetCommandSender(opts: UseHelmetCommandSenderOptions): UseHelmetCommandSender {
  const { robotId, enabled = true, onOffline, onAck, onRejected } = opts

  // Refs so a caller re-creating these handlers each render cannot re-subscribe the websocket —
  // which, mid-call, would tear down the very channel the call is steered on.
  const onRejectedRef = useRef(onRejected)
  onRejectedRef.current = onRejected
  const onOfflineRef = useRef(onOffline)
  onOfflineRef.current = onOffline

  const handleError = useCallback((frame: Envelope) => {
    const code = (frame.payload as { code?: string } | undefined)?.code

    if (code === 'robot_offline') onOfflineRef.current?.()
    // Reported whatever it is. A refusal nobody surfaces is indistinguishable from a command still
    // in flight, and the console waits on it for ever.
    if (code) onRejectedRef.current?.(code)
  }, [])

  // Read through a ref so a caller that re-creates its handler every render does not re-subscribe
  // the websocket — which, mid-call, would tear down the channel the call is being steered on.
  const onAckRef = useRef(onAck)
  onAckRef.current = onAck

  const handleMessage = useCallback((frame: Envelope) => {
    if (frame.type !== 'ack') return
    onAckRef.current?.((frame.payload ?? {}) as HelmetAckPayload, frame)
  }, [])

  const { status, publish } = useLiveChannel({
    // The command topic is subscribed so the broker can answer with an `error` frame; a helmet
    // publishes nothing an operator subscribes to here.
    topics: robotId ? [`robot:${robotId}:command`] : [],
    enabled: enabled && Boolean(robotId) && Boolean(getLiveChannelUrl()),
    onError: handleError,
    onMessage: handleMessage,
  })

  const send = useCallback(
    (intent: HelmetCommandIntent) => {
      if (!robotId) return

      // The intent is published verbatim. Translation to the vendor's `server_push_*` frame happens
      // in the broker's helmet transport, which is the only place that knows the vendor dialect.
      publish(`robot:${robotId}:command`, intent)
    },
    [robotId, publish],
  )

  return { connected: status === 'subscribed', send }
}
